// =============================================================================
// perfil-regiao-belonging.ts — recalculo em tempo real do Indice de Pertencimento.
//
// Proveniencia: Sections.perfilRegiao._bindBelonging (assets/unified-app.js 1106-1131).
// Tres sliders (.u-belong-slider: w1/w2/w3) recalculam, a cada `input`:
//   - os rotulos de peso (#u-belong-w1v/w2v/w3v) em "<v>%";
//   - o score circular (#u-belong-score), clampado a [0,100];
//   - o texto de insight (#u-belong-insight) conforme a soma chegue ou nao a 100%.
//
// Formula FIEL (NAO "corrigir"): score = round(58 + w1*0.18 + w2*0.10 + w3*0.07).
// O texto de insight referencia #u-belong-insight, mas a marcacao da Secao 02 no
// unified.html (535-547) NAO contem esse elemento — ver DESVIO no resumo. Mantido
// o lookup para fidelidade comportamental (insight fica null -> ramo ignorado).
// =============================================================================

import { bindPerfilListenerOnce, queryPerfilNodes } from './perfil-regiao-dom';

/** Ids dos rotulos de peso atualizados a cada slider, na ordem w1/w2/w3 (FIEL). */
const BELONG_WEIGHT_LABEL_IDS = ['u-belong-w1v', 'u-belong-w2v', 'u-belong-w3v'] as const;

/** Le os valores inteiros dos sliders, espelhando `parseInt(value,10) || 0`. */
function readBelongWeights(sliders: readonly HTMLInputElement[]): number[] {
  return sliders.map((slider) => parseInt(slider.value, 10) || 0);
}

/** Atualiza os tres rotulos "<v>%" pelos ids fixos w1v/w2v/w3v (FIEL ao forEach original). */
function paintBelongWeightLabels(weights: readonly number[]): void {
  weights.forEach((weight, position) => {
    const labelId = BELONG_WEIGHT_LABEL_IDS[position];
    if (!labelId) return;
    const labelEl = document.getElementById(labelId);
    if (labelEl) labelEl.textContent = `${weight}%`;
  });
}

/**
 * Score composto FIEL: 58 + w1*0.18 + w2*0.10 + w3*0.07, arredondado.
 * Acesso indexado com `?? 0` so para satisfazer noUncheckedIndexedAccess; com 3
 * sliders sempre presentes o resultado e identico ao `values[i]` do original.
 */
function computeBelongScore(weights: readonly number[]): number {
  /* v8 ignore next -- bindPerfilBelonging aborta sem sliders; weights[0] sempre presente quando chamado (?? 0 defensivo) */
  const w1 = weights[0] ?? 0;
  const w2 = weights[1] ?? 0;
  const w3 = weights[2] ?? 0;
  return Math.round(58 + w1 * 0.18 + w2 * 0.1 + w3 * 0.07);
}

/** Texto de insight conforme a soma dos pesos atinja ou nao 100% (FIEL ao ternario original). */
function belongInsightText(weightsSum: number): string {
  return weightsSum === 100
    ? 'Peso calibrado em 100%. Identidade local é o principal motor de permanência eleitoral na Zona Norte.'
    : `Os pesos somam ${weightsSum}%. Ajuste para 100% para salvar um índice composto comparável entre zonas.`;
}

/** Recalcula rotulos + score + insight a partir do estado atual dos sliders. */
function recalcBelonging(sliders: readonly HTMLInputElement[]): void {
  const weights = readBelongWeights(sliders);
  const weightsSum = weights.reduce((accum, weight) => accum + weight, 0);
  const rawScore = computeBelongScore(weights);
  paintBelongWeightLabels(weights);
  const scoreEl = document.getElementById('u-belong-score');
  if (scoreEl) scoreEl.textContent = String(Math.max(0, Math.min(100, rawScore)));
  const insightEl = document.getElementById('u-belong-insight');
  if (insightEl) insightEl.textContent = belongInsightText(weightsSum);
}

/**
 * Liga o `input` de cada slider (bindOnce, chave 'perfil-belonging') e dispara
 * um recalculo inicial — exatamente como _bindBelonging. No-op sem sliders.
 */
export function bindPerfilBelonging(): void {
  const sliders = queryPerfilNodes<HTMLInputElement>('.u-belong-slider');
  if (!sliders.length) return;
  sliders.forEach((slider) => {
    bindPerfilListenerOnce(slider, 'input', 'perfil-belonging', () => recalcBelonging(sliders));
  });
  recalcBelonging(sliders);
}
